import { useDebounce } from './generic/useDebounce';
import { useEffect, useState } from 'react';
import {
  ISuggestedAddress,
  IDataGouvAddressesResponse,
} from '@monofront/types';
import {
  getMapBoxAutoCompleteAddresses,
  getMapBoxGeocodingFirstCenter,
} from '../utils';
import { useFeatureIsOn } from '../contexts/growthbook';
import { FEATURE_FLAG_MAPBOX_AUTO_COMPLETE } from '../constants';
import { logger } from '@monofront/logging';

type DefaultState = {
  writtenStreet: string;
};
const defaultState: DefaultState = {
  writtenStreet: '',
};

interface ISuggestedAddressesReturn {
  writtenStreet: string;
  setWrittenStreet: (newStreet: string) => void;
  suggestedAddresses?: ISuggestedAddress[];
  isSuggestedAddressLoading: boolean;
}

interface ISuggestedAddressesProps {
  initialState?: DefaultState;
  mapboxToken: string;
  locale: string;
  sessionToken: string;
  searchableCountry: string;
}
const getDataGouvAutoCompleteAddresses = async (
  writtenStreet: string,
): Promise<ISuggestedAddress[]> => {
  const urlParams = new URLSearchParams();

  urlParams.set('q', writtenStreet);

  const response = await fetch(
    'https://api-adresse.data.gouv.fr/search/?' + urlParams,
  );
  const dataGouvAddressesResponse: IDataGouvAddressesResponse =
    await response.json();

  return dataGouvAddressesResponse.features.map(
    ({ properties: { label, name: street, city, postcode } }) => ({
      street,
      city,
      postcode,
      label,
      countryCode: 'FR',
      type: 'address',
    }),
  );
};

export const useSuggestedAddresses = ({
  initialState = defaultState,
  mapboxToken,
  locale,
  sessionToken,
  searchableCountry,
}: ISuggestedAddressesProps): ISuggestedAddressesReturn => {
  const [isSuggestedAddressLoading, setIsSuggestedAddressLoading] =
    useState(false);
  const [writtenStreet, setWrittenStreet] = useState(
    initialState.writtenStreet,
  );
  const [suggestedAddresses, setSuggestedAddresses] =
    useState<ISuggestedAddress[]>();
  const debouncedWrittenStreet = useDebounce(writtenStreet, 100);
  const isMapboxAutocompleteEnabled = useFeatureIsOn(
    FEATURE_FLAG_MAPBOX_AUTO_COMPLETE,
  );
  const [proximity, setProximity] = useState<[string, string] | undefined>(
    undefined,
  );

  const [isProximityFetching, setIsProximityFetching] = useState(false);

  useEffect(() => {
    const getProximity = async () => {
      setIsProximityFetching(true);

      try {
        const center = await getMapBoxGeocodingFirstCenter({
          writtenStreet,
          mapboxToken,
          locale,
          sessionToken,
          searchableCountry,
        });

        setProximity(center);
        setIsProximityFetching(false);
      } catch (error) {
        logger.error(
          'Error while fetching the proximity for the given address',
          {
            error,
            writtenStreet,
          },
        );
        setIsProximityFetching(false);
      }
      // If the address api throws error when there's only numbers

      return;
    };

    if (writtenStreet.length > 3 && !proximity && !isProximityFetching) {
      getProximity();
    }
  }, [writtenStreet]);

  useEffect(() => {
    const getSuggestedStreets = async () => {
      if (!proximity) return;

      if (!debouncedWrittenStreet.match(/.*[a-zA-Z].*/)) {
        // The address api throws error when there's only numbers

        return;
      }

      setIsSuggestedAddressLoading(true);

      const autocompleteAddressesResponse = isMapboxAutocompleteEnabled
        ? await getMapBoxAutoCompleteAddresses(
            {
              writtenStreet,
              mapboxToken,
              locale,
              sessionToken,
              searchableCountry,
              proximity,
            },
            logger,
          )
        : await getDataGouvAutoCompleteAddresses(writtenStreet);

      setSuggestedAddresses(autocompleteAddressesResponse);
      setIsSuggestedAddressLoading(false);
    };

    debouncedWrittenStreet.length > 2 && getSuggestedStreets();
  }, [debouncedWrittenStreet, proximity]);

  return {
    writtenStreet,
    setWrittenStreet,
    suggestedAddresses,
    isSuggestedAddressLoading,
  };
};
